import { useEffect, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { useAuth } from "../../hooks/useAuth";
import { formatDate } from "../../lib/format";
import { clientService } from "../../services/clientService";
import { mealPlanService } from "../../services/mealPlanService";
import type { MealPlan } from "../../types/coaching";

export function ClientMealsPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState<MealPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadPlans() {
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const client = await clientService.getCurrentClient(user.id);
        const assignedPlans = client
          ? await mealPlanService.listAssignedMealPlansForClient(client.id)
          : [];
        if (mounted) setPlans(assignedPlans);
      } catch (caughtError) {
        console.error("Unable to load assigned meal plans", caughtError);
        if (mounted) setError("Unable to load your meal plans.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void loadPlans();

    return () => {
      mounted = false;
    };
  }, [user]);

  return (
    <div>
      <AdminPageHeader
        eyebrow="Client portal"
        title="Your meal plan"
        description="Read-only meal plans assigned by your coach."
      />
      {loading ? <FullPageLoader label="Loading meals" /> : null}
      {error ? <EmptyState title="Unable to load meals.">{error}</EmptyState> : null}
      {!loading && !error && !plans.length ? (
        <EmptyState title="Your coach has not assigned a meal plan yet." />
      ) : null}
      <div className="space-y-5">
        {plans.map((plan) => (
          <AdminPanel
            description={`Assigned ${formatDate(plan.assigned_at)}${plan.starts_on ? `, starts ${formatDate(plan.starts_on)}` : ""}`}
            key={plan.id}
            title={plan.title}
          >
            <p className="mb-4 text-sm leading-6 text-graphite/75">{plan.description || "No description."}</p>
            <p className="mb-5 text-sm text-graphite/70">
              {plan.target_calories ? `${plan.target_calories} kcal` : "No calorie target"}
              {plan.target_protein_grams ? `, ${plan.target_protein_grams}g protein` : ""}
              {plan.target_carbs_grams ? `, ${plan.target_carbs_grams}g carbs` : ""}
              {plan.target_fat_grams ? `, ${plan.target_fat_grams}g fat` : ""}
            </p>
            <div className="space-y-4">
              {(plan.meal_plan_days ?? []).map((day) => (
                <div className="rounded-md border border-line bg-white p-4" key={day.id}>
                  <h2 className="font-semibold text-ink">{day.title}</h2>
                  {day.notes ? <p className="mt-1 text-sm text-graphite/70">{day.notes}</p> : null}
                  <div className="mt-3 space-y-2">
                    {(day.meal_plan_items ?? []).map((item) => (
                      <div className="rounded-md bg-field p-3 text-sm" key={item.id}>
                        <p className="font-medium text-ink">{item.meal_name}</p>
                        <p className="mt-1 whitespace-pre-wrap text-graphite/70">{item.food_details}</p>
                        <p className="mt-1 text-graphite/70">
                          {[item.calories ? `${item.calories} kcal` : null, item.protein_grams ? `${item.protein_grams}g protein` : null, item.carbs_grams ? `${item.carbs_grams}g carbs` : null, item.fat_grams ? `${item.fat_grams}g fat` : null].filter(Boolean).join(" - ")}
                        </p>
                        {item.alternatives ? <p className="mt-1 text-graphite/70">Alternatives: {item.alternatives}</p> : null}
                        {item.notes ? <p className="mt-1 text-graphite/70">{item.notes}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </AdminPanel>
        ))}
      </div>
    </div>
  );
}
